<template>
  <div class="wallet-connect">
    <div v-if="!walletStore.isConnected" class="connect-section">
      <button
        @click="handleConnect"
        :disabled="walletStore.isConnecting"
        class="connect-btn"
      >
        <svg v-if="walletStore.isConnecting" class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        {{ walletStore.isConnecting ? 'Connecting...' : 'Connect Wallet' }}
      </button>
    </div>

    <div v-else class="connected-section">
      <div class="wallet-card">
        <!-- Wallet Header with Address and Disconnect Button -->
        <div class="wallet-header">
          <div class="wallet-address">
            <div class="address-icon">
              <svg class="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path>
              </svg>
            </div>
            <div class="address-text">
              <span class="address-label">Connected</span>
              <span class="address">{{ walletStore.shortAddress }}</span>
            </div>
          </div>
          <button @click="walletStore.disconnectWallet" class="disconnect-btn-header">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"></path>
            </svg>
          </button>
        </div>
        
        <!-- Network Warning -->
        <div v-if="!walletStore.isOnXLayer" class="chain-warning">
          <div class="warning-message">
            <svg class="w-5 h-5 text-yellow-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"></path>
            </svg>
            <span>Please switch to X Layer network</span>
          </div>
          <button @click="handleSwitchNetwork" class="switch-btn">
            Switch Network
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted } from 'vue'
import { useWalletStore } from '@/stores/wallet'
import { toast } from '@/utils/toast'

const walletStore = useWalletStore()

// Check for existing connection on component mount
onMounted(() => {
  walletStore.checkConnection()
})

const handleConnect = async () => {
  try {
    await walletStore.connectWallet()
  } catch (error: any) {
    toast.error('Connection Failed', error.message || 'Failed to connect wallet')
  }
}

const handleSwitchNetwork = async () => {
  try {
    await walletStore.switchToXLayer()
  } catch (error: any) {
    toast.error('Network Switch Failed', error.message || 'Failed to switch network')
  }
}
</script>

<style scoped>
.wallet-connect {
  @apply flex flex-col items-center space-y-4;
}

.connect-section {
  @apply w-full;
}

.connect-btn {
  @apply w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold py-3 px-6 rounded-lg transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center;
}

.connected-section {
  @apply w-full;
}

.wallet-card {
  @apply bg-gray-800 rounded-lg p-4 border border-gray-700;
}

.wallet-header {
  @apply flex items-center justify-between;
}

.wallet-address {
  @apply flex items-center space-x-3;
}

.address-icon {
  @apply flex-shrink-0;
}

.address-text {
  @apply flex flex-col;
}

.address-label {
  @apply text-green-400 text-xs font-medium uppercase tracking-wide;
}

.address {
  @apply text-white font-mono text-sm mt-1;
}

.disconnect-btn-header {
  @apply p-2 text-gray-400 hover:text-red-400 hover:bg-gray-700 rounded-lg transition-all duration-200 flex-shrink-0;
}

.chain-warning {
  @apply bg-yellow-900 border border-yellow-600 rounded-lg p-3 space-y-2 mt-4;
}

.warning-message {
  @apply flex items-center space-x-2 text-yellow-200 text-sm;
}

.switch-btn {
  @apply w-full bg-yellow-600 hover:bg-yellow-700 text-white font-semibold py-2 px-4 rounded transition-colors duration-200;
}
</style>